import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { schoolApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { GraduationCap, Users, School, ClipboardList, TrendingUp, AlertTriangle } from 'lucide-react';

export default function SchoolDashboard() {
  const { tenant } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    schoolApi.getDashboard()
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const studentPercent = stats ? Math.round((stats.students / stats.max_students) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="school-dashboard">
      <div>
        <h1 className="text-2xl font-bold text-stone-900">Tableau de bord</h1>
        <p className="text-sm text-stone-500 mt-0.5">Bienvenue sur votre espace {tenant?.name}</p>
      </div>

      {/* Plan warning */}
      {stats && studentPercent >= 80 && (
        <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="plan-warning">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">
              {studentPercent >= 100 ? 'Limite d\'eleves atteinte' : 'Vous approchez de la limite'}
            </p>
            <p className="text-xs text-amber-600">
              {stats.students}/{stats.max_students} eleves ({studentPercent}%). Plan: {stats.plan}.
            </p>
          </div>
        </div>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Eleves', value: stats?.students ?? 0, icon: GraduationCap, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'Enseignants', value: stats?.teachers ?? 0, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Classes', value: stats?.classes ?? 0, icon: School, color: 'text-violet-600', bg: 'bg-violet-50' },
          { label: 'Evaluations (aujourd\'hui)', value: stats?.evaluations_today ?? 0, icon: ClipboardList, color: 'text-amber-600', bg: 'bg-amber-50' },
        ].map((s, i) => (
          <Card key={i} className="border-stone-200" data-testid={`dashboard-stat-${i}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center`}>
                  <s.icon className={`w-4 h-4 ${s.color}`} />
                </div>
              </div>
              <p className="text-2xl font-bold text-stone-900">{s.value}</p>
              <p className="text-xs text-stone-500 mt-0.5">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Capacity bar */}
      {stats && (
        <Card className="border-stone-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-stone-900">Capacite eleves</p>
              <p className="text-sm text-stone-500">{stats.students} / {stats.max_students}</p>
            </div>
            <div className="w-full h-2.5 bg-stone-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${studentPercent >= 90 ? 'bg-red-500' : studentPercent >= 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                style={{ width: `${Math.min(studentPercent, 100)}%` }}
                data-testid="capacity-bar"
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
